/**
 * LspClient registry — non-editor surfaces reach the live LSP worker
 * (WYSIWYG plan §6, W1 increment).
 *
 * The Monaco bridge (and thus the JSON-RPC connection) is booted inside the
 * Editor primitive — monaco loads lazily there, so full ownership hoisting is
 * deferred; instead the Editor PUBLISHES its live {@link BridgeHandle} here,
 * keyed by store instance (same WeakMap pattern as editApplier). The details
 * pane consumes `prepareRename`/`rename`; affordances stay disabled while no
 * handle is live (no editor mounted / worker still booting).
 *
 * Everything speaks STORE paths ("/"-normalized internally); LSP positions
 * are 0-based, engine spans 1-based — conversion happens here, at the edge.
 */

import { useSyncExternalStore } from "react";
import type { BridgeHandle } from "./monaco/lspBridge.js";
import { workerUriForPath, pathFromWorkerUri } from "./monaco/lspBridge.js";

interface LspRange {
  start: { line: number; character: number };
  end: { line: number; character: number };
}

interface LspWorkspaceEdit {
  changes?: Record<string, { range: LspRange; newText: string }[]>;
  documentChanges?: {
    textDocument?: { uri: string };
    edits?: { range: LspRange; newText: string }[];
  }[];
}

export interface RenameFileEdits {
  /** "/"-prefixed workspace path. */
  readonly path: string;
  /** UTF-16 offset edits against that file's text AT REQUEST TIME. */
  readonly edits: readonly { start: number; end: number; newText: string }[];
}

export interface LspRenameClient {
  /** prepareRename: null when the position isn't renamable. */
  prepare(path: string, line1: number, column1: number): Promise<{ placeholder: string } | null>;
  /**
   * textDocument/rename → per-file OFFSET edits, converted against
   * `textsByPath` (the caller's captured file texts — the same snapshot the
   * apply guard verifies, so span math and guard share one truth).
   */
  rename(
    path: string,
    line1: number,
    column1: number,
    newName: string,
    textsByPath: ReadonlyMap<string, string>,
  ): Promise<readonly RenameFileEdits[] | null>;
}

const handles = new WeakMap<object, BridgeHandle>();
const listeners = new WeakMap<object, Set<() => void>>();

function notify(store: object): void {
  for (const l of listeners.get(store) ?? []) l();
}

/** Editor publishes its live handle (on bridge start) / retracts it (dispose). */
export function registerLspHandle(store: object, handle: BridgeHandle): void {
  handles.set(store, handle);
  notify(store);
}

export function unregisterLspHandle(store: object, handle: BridgeHandle): void {
  if (handles.get(store) !== handle) return; // a newer handle took over
  handles.delete(store);
  notify(store);
}

export function getLspHandle(store: object): BridgeHandle | null {
  return handles.get(store) ?? null;
}

/** Reactive presence of a live LSP handle (enables rename affordances). */
export function useLspClient(store: object): LspRenameClient | null {
  const subscribe = (cb: () => void): (() => void) => {
    let set = listeners.get(store);
    if (!set) {
      set = new Set();
      listeners.set(store, set);
    }
    set.add(cb);
    return () => set.delete(cb);
  };
  const handle = useSyncExternalStore(subscribe, () => handles.get(store) ?? null, () => null);
  return handle ? clientFor(handle) : null;
}

function abs(path: string): string {
  return path.startsWith("/") ? path : "/" + path;
}

function offsetAt(text: string, pos: { line: number; character: number }): number {
  let off = 0;
  let line = 0;
  while (line < pos.line) {
    const nl = text.indexOf("\n", off);
    if (nl === -1) return text.length;
    off = nl + 1;
    line++;
  }
  // Clamp to the LINE end (LSP spec): a character past the line's length must
  // not spill into the next line.
  const lineEnd = text.indexOf("\n", off);
  const cap = lineEnd === -1 ? text.length : lineEnd;
  return Math.min(off + pos.character, cap);
}

function clientFor(handle: BridgeHandle): LspRenameClient {
  return {
    async prepare(path, line1, column1) {
      const r = (await handle.connection.sendRequest("textDocument/prepareRename", {
        textDocument: { uri: workerUriForPath(abs(path)) },
        position: { line: line1 - 1, character: column1 - 1 },
      })) as { placeholder?: string } | null;
      if (!r) return null;
      return { placeholder: r.placeholder ?? "" };
    },

    async rename(path, line1, column1, newName, textsByPath) {
      const we = (await handle.connection.sendRequest("textDocument/rename", {
        textDocument: { uri: workerUriForPath(abs(path)) },
        position: { line: line1 - 1, character: column1 - 1 },
        newName,
      })) as LspWorkspaceEdit | null;
      if (!we) return null;

      const byPath = new Map<string, { start: number; end: number; newText: string }[]>();
      const add = (uri: string, edits: { range: LspRange; newText: string }[] | undefined): boolean => {
        if (!edits) return true;
        const p = pathFromWorkerUri(uri);
        const text = textsByPath.get(p);
        if (text === undefined) return false; // touches a file the caller didn't capture
        const list = byPath.get(p) ?? [];
        for (const e of edits) {
          list.push({
            start: offsetAt(text, e.range.start),
            end: offsetAt(text, e.range.end),
            newText: e.newText,
          });
        }
        byPath.set(p, list);
        return true;
      };

      for (const [uri, edits] of Object.entries(we.changes ?? {})) {
        if (!add(uri, edits)) return null;
      }
      for (const dc of we.documentChanges ?? []) {
        if (dc.textDocument?.uri && !add(dc.textDocument.uri, dc.edits as never)) return null;
      }
      return [...byPath.entries()].map(([p, edits]) => ({ path: p, edits }));
    },
  };
}
